/**
 * Zustand auth-store (Stage 5.X.2).
 *
 * **In-memory only** (без persist) — по `docs/tg-miniapp.md` §1.6 + §1.7:
 *   - JWT хранится в memory (closeable tab = forgotten);
 *   - refresh-токен тоже в memory (мы НЕ полагаемся на персистенцию,
 *     потому что Telegram WebApp всегда заново выдаёт `initData` при
 *     открытии — можно сделать новый `/auth/login` без потери UX).
 *
 * State machine:
 *
 *   idle ─── login() ───▶ loading ──── success ───▶ authenticated
 *      ▲                    │                          │
 *      │                    └── fail ───▶ error        │ logout()
 *      └────────────────────────────────────────────────┘
 *
 * `error` тоже может транзитнуть в `loading` через retry.
 *
 * Stage 5.X.10 (hardening) — рассмотрим httpOnly cookie для refresh
 * (требует backend changes + CORS adjustment). Сейчас trade-off
 * приемлем: атакующий с XSS получит refresh, но access-токен и так
 * имеет TTL 15 мин и привязан к подписи; ущерб ограничен.
 */

import { create } from 'zustand';

import type { UserPublicView } from '@/api/types';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  /** Момент истечения access_token (epoch ms); `null` если не залогинены. */
  accessExpiresAt: number | null;
  user: UserPublicView | null;
  status: AuthStatus;
  /** Последняя ошибка (для UI; чистится на новом `login()`). */
  error: string | null;

  // ----- Actions -----

  /**
   * Установить новую пару токенов + юзера (после /auth/login или /auth/refresh).
   *
   * Side effects: переключает `status` на `authenticated`, сбрасывает `error`.
   */
  setSession(input: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: UserPublicView;
  }): void;

  /**
   * Обновить только пару токенов (после /auth/refresh — юзер не возвращается).
   *
   * Если store пуст (`user === null`) — no-op (caller обязан сначала
   * сделать full login).
   */
  setTokens(input: { accessToken: string; refreshToken: string; expiresIn: number }): void;

  /**
   * Перевести store в `loading` (на старте login/refresh-операции).
   */
  setLoading(): void;

  /**
   * Перевести store в `error` с заданным сообщением.
   */
  setError(message: string): void;

  /**
   * Очистить всё: tokens, user, status='idle', error=null.
   *
   * Используется при logout() и при finally-cleanup на refresh-fail.
   */
  clear(): void;
}

/**
 * Главный auth-хук. Используется хуками (useAuth), HTTP-клиентом и
 * компонентами (AuthGuard).
 *
 * Безопасно: `create` инициализирует state один раз; HMR в dev пересоздаёт
 * только при изменении этого файла (см. zustand 5 + Vite HMR notes).
 */
export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  refreshToken: null,
  accessExpiresAt: null,
  user: null,
  status: 'idle',
  error: null,

  setSession: ({ accessToken, refreshToken, expiresIn, user }) =>
    set({
      accessToken,
      refreshToken,
      accessExpiresAt: Date.now() + expiresIn * 1000,
      user,
      status: 'authenticated',
      error: null,
    }),

  setTokens: ({ accessToken, refreshToken, expiresIn }) =>
    set((state) => {
      if (state.user === null) {
        // Защита от race: refresh без предшествующего login — невозможно.
        return state;
      }
      return {
        accessToken,
        refreshToken,
        accessExpiresAt: Date.now() + expiresIn * 1000,
        status: 'authenticated',
        error: null,
      };
    }),

  setLoading: () => set({ status: 'loading', error: null }),

  setError: (message) => set({ status: 'error', error: message }),

  clear: () =>
    set({
      accessToken: null,
      refreshToken: null,
      accessExpiresAt: null,
      user: null,
      status: 'idle',
      error: null,
    }),
}));

// ---------------------------------------------------------------------------
// Selectors (для оптимизированных подписок — не ребайндят весь компонент)
// ---------------------------------------------------------------------------

export const selectAccessToken = (s: AuthState): string | null => s.accessToken;
export const selectRefreshToken = (s: AuthState): string | null => s.refreshToken;
export const selectUser = (s: AuthState): UserPublicView | null => s.user;
export const selectStatus = (s: AuthState): AuthStatus => s.status;
export const selectIsAuthenticated = (s: AuthState): boolean =>
  s.status === 'authenticated' && s.accessToken !== null;
